import React, { useState, useEffect, useRef } from "react";
import { TextField, PrimaryButton, DefaultButton, Dropdown, IDropdownOption, Stack, Callout, TooltipHost, DirectionalHint, Checkbox, IconButton } from "@fluentui/react";
import styles from "./DocFieldSelectionV2.module.css";
import { toast } from "react-toastify";
import { gfileassourceoption, gextrprop, extrtypesarray, extrtypes } from "../../interfaces";
import {
    textFieldStylesDocExtr,
    textFieldStylesMultilineDocExtrDPA,
    dropdownStylesDocExtr,
    textFieldStylesDocExtractImportScheme,
    calloutStylesExportDocCallout,
    dropdownFileSrcStylesDocExtr,
    tooltipStyles,
    dropdownFileNativeColorSrcStylesDocExtr,
    customPropInvIsKeyCheckStyles,
    srcKeyCalloutStyles,
    srcKeyCalloutInnerStyles
} from "../../styles_glob";
import { FaTrashCan } from "react-icons/fa6";
import { assistantCodes, extrTypeOptions, emptyValSrcName } from "../../lsts";
import { MdAddCircleOutline } from "react-icons/md";
import { BiChevronRight, BiChevronDown, BiCollapseVertical, BiExpandVertical } from "react-icons/bi";
import { safelySelectProp, getVSourceFid, checkVSSourceIDExists } from "../../util_glob";
import { TbArrowAutofitDown, TbArrowAutofitUp } from "react-icons/tb";
import { MdDragIndicator } from "react-icons/md";
import  InfoStrings  from "../InfoStrings/InfoStrings";
import { FaKey } from "react-icons/fa";

interface PropertiesEditorProps {
    initialProperties?: gextrprop[] | [];
    onChange: (updatedProperties: gextrprop[]) => void;
    isRoot?: boolean;
    fileList?: gfileassourceoption[];
    comp_title?: string;
    comp_subtitle?: string;
    assistanttype?: string;
}

{/* used in dpa schema edit & invoice custom fields edit */}
export const DocFieldSelectionV2: React.FC<PropertiesEditorProps> = ({ initialProperties, onChange, isRoot, fileList, comp_title, comp_subtitle, assistanttype= '' }) => {
    const [prprts, setProperties] = useState<gextrprop[]>(initialProperties || []);
    const [isCalloutVisible, setIsCalloutVisible] = useState(false);
    const [importJson, setImportJson] = useState('');
    const [calloutTarget, setCalloutTarget] = useState<HTMLElement | null>(null);
    const [isInv, setIsInv] = useState(assistanttype == assistantCodes.ipa ? true : false);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [collapsedItems, setCollapsedItems] = useState<Set<number>>(new Set());
    const [srcKeyCalloutIndex, setSrcKeyCalloutIndex] = useState<number | null>(null);
    const [srcKeyCalloutTarget, setSrcKeyCalloutTarget] = useState<HTMLElement | null>(null);
    const dragNodeRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        setProperties(initialProperties || []);
        import.meta.env.DEV && console.log("‹assistanttype›", initialProperties);
        setIsInv(assistanttype == assistantCodes.ipa ? true : false);
    }, [initialProperties]);

    const parseJsonToProperties = (parsedJson: any): gextrprop[] => {
        if (!Array.isArray(parsedJson)) {
            let name        = safelySelectProp(parsedJson, "name", '');
            let description = safelySelectProp(parsedJson, "description", '');
            let type        = safelySelectProp(parsedJson, "type", '');
            let items       = safelySelectProp(parsedJson, "items", []);

            let property: gextrprop = { name, description, type };
            if (type === "object") property.items = parseJsonToProperties(items);
            return [property];
        } else {
            return parsedJson.map((item: any) => {
                if (typeof item !== "object" || item === null) throw new Error("Invalid property format. Expected an object.");

                let name        = safelySelectProp(item, "name", '');
                let description = safelySelectProp(item, "description", '');
                let type        = safelySelectProp(item, "type", '');
                let items       = safelySelectProp(item, "items", []);
                let validation_source = safelySelectProp(item, "validation_source", '');

                if (typeof name !== "string" || typeof description !== "string" || !extrtypesarray.includes(type)) throw new Error("Invalid property fields.");

                validation_source = '';
                let property: gextrprop = { name, description, type };

                if (item.type == "string" || item.type == "number") property = { name, description, type, validation_source };
                if (type === "array" && Array.isArray(items)) property.items = parseJsonToProperties(items);
                if (type === "object") property.items = parseJsonToProperties(items);

                return property;
            });
        }
    }

    const handleUpdate = (index: number, updatedFields: Partial<gextrprop>) => {
        const newProps = [...prprts];
        if (updatedFields.name) updatedFields.name = updatedFields.name ? updatedFields.name.replace(/\s+/g, "_").trim() : updatedFields.name;
        newProps[index] = { ...newProps[index], ...updatedFields };
        setProperties(newProps);

        onChange(newProps);
    };

    const handleRemove = (index: number) => {
        const newProps = [...prprts];
        newProps.splice(index, 1);
        setProperties(newProps);
        onChange(newProps);
    };

    const handleAdd = () => {
        const newProps = [
            ...(prprts || []),
            {
                name: '',
                description: '',
                validation_source: '',
                type: "string" as "string",
                items: []
            }
        ];
        setProperties(newProps);
        onChange(newProps);
    };

    const handleImport = () => {
        const parsedJson = JSON.parse(importJson);
        import.meta.env.DEV && console.log("‹import_json_scheme›", parsedJson);
        let newProps = parseJsonToProperties(parsedJson);
        setProperties(newProps);
        onChange(newProps);
        setIsCalloutVisible(false);
        toast.info('Scheme imported.', { position: "top-center", autoClose: 2000 });
    };
    const handleExport = () => {
        const schemaJson = JSON.stringify(prprts, null, 2);
        navigator.clipboard.writeText(schemaJson).then(() => {
            // Optional: Add a success notification here
            toast.info('Schema copied to clipboard', { position: "top-center", autoClose: 2000 });
        }).catch(err => {
            console.error('Failed to copy schema:', err);
            alert('Failed to copy schema to clipboard');
        });
    };

    const importScheme = (event: React.MouseEvent<HTMLElement>) => {
        setCalloutTarget(event.currentTarget);
        setIsCalloutVisible(true);
    };

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        setDraggedIndex(index);
        // Find the parent fieldContainer to apply styling
        const fieldContainer = e.currentTarget.closest(`.${styles.fieldContainer}`) as HTMLElement;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        dragNodeRef.current = fieldContainer;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
        // Add a slight delay to allow the drag image to be captured before adding the dragging class
        setTimeout(() => {
            if (dragNodeRef.current) {
                dragNodeRef.current.classList.add(styles.dragging);
            }
        }, 0);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (draggedIndex !== null && draggedIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        // Only clear dragOverIndex if we're leaving the container entirely
        const relatedTarget = e.relatedTarget as HTMLElement;
        if (!e.currentTarget.contains(relatedTarget)) {
            setDragOverIndex(null);
        }
    };

    const handleDragEnd = () => {
        if (dragNodeRef.current) {
            dragNodeRef.current.classList.remove(styles.dragging);
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
        dragNodeRef.current = null;
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) {
            handleDragEnd();
            return;
        }

        const newProps = [...prprts];
        const [draggedItem] = newProps.splice(draggedIndex, 1);
        newProps.splice(dropIndex, 0, draggedItem);

        setProperties(newProps);
        onChange(newProps);
        handleDragEnd();
    };

    const toggleCollapse = (index: number) => {
        setCollapsedItems(prev => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    const getItemsPreview = (items: gextrprop[] | undefined): string => {
        if (!items || items.length === 0) return "(empty)";
        const names = items.map(p => p.name || "unnamed").slice(0, 4);
        const preview = names.join(", ");
        return items.length > 4 ? `${preview} +${items.length - 4} more` : preview;
    };

    const hasCollapsibleItems = prprts.some(p => p.type === "array" || p.type === "object");

    const collapseAll = () => {
        const indices = new Set<number>();
        prprts.forEach((p, i) => {
            if (p.type === "array" || p.type === "object") indices.add(i);
        });
        setCollapsedItems(indices);
    };

    const expandAll = () => {
        setCollapsedItems(new Set());
    };

    const onRenderTitle = (options?: IDropdownOption[]) => {
        const selectedItem = options && options[0];
        return (
            <span className={selectedItem?.key === '' ? "placeholder" : ''} style={{color: selectedItem?.key === '' ? "gray" : "#333", fontStyle: selectedItem?.key === '' ? "italic" : "normal"}}>
                {selectedItem?.text || emptyValSrcName}
            </span>
        );
    };

    return (
        <div className={styles.containerDocFieldSelV2}>
            {isCalloutVisible && (
                <Callout
                    target={calloutTarget}
                    onDismiss={() => setIsCalloutVisible(false)}
                    directionalHint={DirectionalHint.bottomRightEdge}
                    gapSpace={10}
                    isBeakVisible={false}
                    styles={calloutStylesExportDocCallout}
                >
                    <div style={{ paddingTop: 5, paddingLeft: 20, paddingRight: 20, paddingBottom: 10 }}>
                        <TextField
                            className={styles.calloutImportScheme}
                            label="Paste JSON Scheme"
                            multiline
                            rows={5}
                            value={importJson}
                            onChange={(_, newValue) => setImportJson(newValue || '')}
                            styles={textFieldStylesDocExtractImportScheme}
                        />
                        <PrimaryButton text="Import" onClick={handleImport} style={{ marginTop: 10 }} />
                    </div>
                </Callout>
            )}
            {isRoot && (
                <div className={styles.importScheme}>
                    <h3 className={styles.extrDefTitle}>{comp_title || "Content Structure"}</h3>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <div className={styles.importSchemeImportButton} onClick={importScheme}>
                            <TooltipHost content="Import Extraction Scheme. You can also copy scheme from existing files" directionalHint={DirectionalHint.bottomRightEdge} hostClassName={styles.importSchemeImportToolHost}>
                                <TbArrowAutofitUp size={15} />
                            </TooltipHost>
                        </div>
                        <div className={styles.importSchemeImportButton} onClick={handleExport}>
                            <TooltipHost content="Export schema to clipboard" directionalHint={DirectionalHint.bottomRightEdge} hostClassName={styles.importSchemeImportToolHost}>
                                <TbArrowAutofitDown size={15} />
                            </TooltipHost>
                        </div>
                        {hasCollapsibleItems && (
                            <>
                                <div className={styles.importSchemeImportButton} onClick={collapseAll}>
                                    <TooltipHost content="Collapse all nested fields" directionalHint={DirectionalHint.bottomRightEdge} hostClassName={styles.importSchemeImportToolHost}>
                                        <BiCollapseVertical size={15} />
                                    </TooltipHost>
                                </div>
                                <div className={styles.importSchemeImportButton} onClick={expandAll}>
                                    <TooltipHost content="Expand all nested fields" directionalHint={DirectionalHint.bottomRightEdge} hostClassName={styles.importSchemeImportToolHost}>
                                        <BiExpandVertical size={15} />
                                    </TooltipHost>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {prprts.length == 0 && (
                <div className={styles.noExtractDefinitionsContainer}>
                    <div className={styles.noExtractDefinitionsText}>{comp_subtitle || "Click 'Add New Field' to start defining the fields you want to get extracted"}</div>
                </div>
            )}

            {prprts.length > 0 &&
                prprts.map((prop, index) => (
                    <div
                        key={index}
                        className={`${styles.fieldContainer} ${dragOverIndex === index ? styles.dragOver : ''}`}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, index)}
                    >
                        <Stack tokens={{ childrenGap: 5 }}>
                            <div className={styles.stackContainer}>
                                <div
                                    className={styles.dragHandle}
                                    title="Drag to reorder"
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                >
                                    <MdDragIndicator size={18} />
                                </div>
                                <div className={styles.fieldRow}>
                                    <TextField
                                        placeholder="Field Name"
                                        value={prop?.name ?? ''}
                                        invalid={prop?.name?.trim() == ''}
                                        onChange={e => handleUpdate(index, { name: (e.target as HTMLInputElement).value })}
                                        className={`${styles.fieldInput} ${styles.fieldColumnName}`}
                                        styles={textFieldStylesDocExtr}
                                    />
                                    <Dropdown
                                        selectedKey={prop.type}
                                        options={extrTypeOptions}
                                        onChange={(e, option) => handleUpdate(index, { type: option?.key as extrtypes })}
                                        className={`${styles.fieldInput} ${styles.fieldColumn}`}
                                        styles={dropdownFileNativeColorSrcStylesDocExtr }
                                    />
                                    <div className={styles.editSrcEditor}>
                                        {prop.type === "string" || prop.type === "number" ? (
                                            <div className={styles.editSrcEl}>
                                                <Dropdown
                                                    selectedKey={prop.validation_source || ''}
                                                    placeholder="Source"
                                                    onRenderTitle={onRenderTitle}
                                                    options={[
                                                        { key: '', text: '' }, // empty
                                                        ...(getVSourceFid(fileList || []))
                                                    ]}
                                                    onChange={(e, option) => {
                                                        const newValSrc = option?.key?.toString();
                                                        const update: Partial<gextrprop> = { validation_source: newValSrc };
                                                        if (!newValSrc || newValSrc === emptyValSrcName) update.filter_column = '';
                                                        handleUpdate(index, update);
                                                    }}
                                                    className={`${styles.fieldInput} ${styles.fieldColumn}`}
                                                    styles={dropdownFileNativeColorSrcStylesDocExtr}
                                                    disabled={!isInv} // disable for non-inv operator
                                                />
                                            </div>

                                        ) : null}
                                        {(prop.type === "string" || prop.type === "number") && prop.validation_source && prop.validation_source !== emptyValSrcName && checkVSSourceIDExists(fileList || [], prop?.validation_source || '') ? (
                                            <div className={styles.editSrcElConfWrap}>

                                                <IconButton
                                                    iconProps={{ iconName: "Settings"}}
                                                    styles={{
                                                        icon: {
                                                            fontSize: 15,
                                                        },
                                                    }}
                                                    className={styles.editButtonSrcConf}
                                                    title="Source Key"
                                                    ariaLabel="Source Key"
                                                    onClick={(e) => {
                                                        setSrcKeyCalloutTarget(e.currentTarget as HTMLElement);
                                                        setSrcKeyCalloutIndex(index);
                                                    }}
                                                />
                                            </div>
                                        ) : null}
                                    </div>
                                    {srcKeyCalloutIndex === index && (
                                        <Callout
                                            target={srcKeyCalloutTarget}
                                            onDismiss={() => setSrcKeyCalloutIndex(null)}
                                            directionalHint={DirectionalHint.bottomLeftEdge}
                                            gapSpace={6}
                                            isBeakVisible={true}
                                            setInitialFocus
                                            styles={srcKeyCalloutStyles}
                                        >
                                            <Stack tokens={{ childrenGap: 8 }} className={srcKeyCalloutInnerStyles.container}>
                                                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                                                    <h4 className={srcKeyCalloutInnerStyles.title}>Source Data Config</h4>
                                                    <IconButton
                                                        iconProps={{ iconName: "Cancel" }}
                                                        title="Close"
                                                        ariaLabel="Close"
                                                        className={styles.closeButtonSrcConf}
                                                        onClick={() => setSrcKeyCalloutIndex(null)}
                                                    />
                                                </Stack>

                                                <TextField
                                                    label="Source Key - The name of the field on the source that should correspond to this field."
                                                    value={prop.filter_column ?? ''}
                                                    onChange={e => handleUpdate(index, { filter_column: (e.target as HTMLInputElement).value })}
                                                    styles={textFieldStylesDocExtr}
                                                />
                                            </Stack>
                                        </Callout>
                                    )}
                                </div>
                                {
                                    isInv == true && (
                                        <TooltipHost content={<InfoStrings src_id={"inv_conf"} str_id="is_key" />} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles} className={styles.invIsKeyCheck}>
                                            <Checkbox
                                                className={styles.invRequiredCheck}
                                                label="Key"
                                                styles={customPropInvIsKeyCheckStyles}
                                                disabled={false}
                                                checked={prop.identificator ?? false}
                                                onChange={(e, newValue) => handleUpdate(index, { identificator: newValue || false })}
                                            />
                                        </TooltipHost>
                                    )
                                }
                                <TextField
                                    value={prop.description}
                                    onChange={e => handleUpdate(index, { description: (e.target as HTMLInputElement).value })}
                                    multiline
                                    placeholder="Description of field purpose for AI"
                                    className={`${styles.fieldInputTT} ${styles.fieldInputFullWidth}`}
                                    styles={textFieldStylesMultilineDocExtrDPA}
                                    resizable={false}
                                    autoAdjustHeight={true}
                                />
                                <div className={styles.removeButtonContainer}>
                                    <div onClick={() => handleRemove(index)} className={styles.removeButton}>
                                        <FaTrashCan size={12} />
                                    </div>

                                    { !isInv && (
                                        <TooltipHost content="Identificator" directionalHint={DirectionalHint.bottomCenter} calloutProps={tooltipStyles}>
                                            <Checkbox
                                                className={styles.identificatorCheck}
                                                styles={customPropInvIsKeyCheckStyles}
                                                ariaLabel="Key"
                                                checked={prop.identificator ?? false}
                                                onChange={(e, newValue) => handleUpdate(index, { identificator: newValue || false })}
                                            />
                                        </TooltipHost>
                                    )}
                                </div>
                            </div>
                            {prop.type === "array" && (
                                <div className={styles.arrayContainer}>
                                    <div
                                        className={styles.objectHeader}
                                        onClick={() => toggleCollapse(index)}
                                    >
                                        <span className={styles.collapseToggle}>
                                            {collapsedItems.has(index) ? <BiChevronRight size={16} /> : <BiChevronDown size={16} />}
                                        </span>
                                        <h4>List Items</h4>
                                        {collapsedItems.has(index) && (
                                            <span className={styles.collapsedPreview}>{getItemsPreview(prop.items)}</span>
                                        )}
                                    </div>
                                    <div className={collapsedItems.has(index) ? styles.collapseWrapperCollapsed : styles.collapseWrapperExpanded}>
                                        <div className={styles.collapseInner}>
                                            <DocFieldSelectionV2
                                                initialProperties={prop.items || []}
                                                onChange={subProps => {
                                                    handleUpdate(index, { items: subProps });
                                                }}
                                                fileList={fileList}
                                                assistanttype={assistanttype}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {prop.type === "object" && (
                                <div className={styles.arrayContainer}>
                                    <div
                                        className={styles.objectHeader}
                                        onClick={() => toggleCollapse(index)}
                                    >
                                        <span className={styles.collapseToggle}>
                                            {collapsedItems.has(index) ? <BiChevronRight size={16} /> : <BiChevronDown size={16} />}
                                        </span>
                                        <h4>Object</h4>
                                        {collapsedItems.has(index) && (
                                            <span className={styles.collapsedPreview}>{getItemsPreview(prop.items)}</span>
                                        )}
                                    </div>
                                    <div className={collapsedItems.has(index) ? styles.collapseWrapperCollapsed : styles.collapseWrapperExpanded}>
                                        <div className={styles.collapseInner}>
                                            <DocFieldSelectionV2
                                                initialProperties={prop.items || []}
                                                onChange={subProps => {
                                                    handleUpdate(index, { items: subProps });
                                                }}
                                                fileList={fileList}
                                                assistanttype={assistanttype}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Stack>
                    </div>
                ))}
            <PrimaryButton onClick={handleAdd} className={styles.addButtonV2}>
                <div className={styles.addButtonV2Text}>
                    <MdAddCircleOutline size={24} />
                </div>
                Add Field
            </PrimaryButton>
        </div>
    );
};
